import React, { useMemo, useRef, useEffect } from 'react';
import { List } from 'react-window';
import {
  Box,
  Typography,
  Stack,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { getCategoryColor } from './categoryPalette';

const ROW_HEIGHT = 64;

// Build the list of vendors from the cells, applying the same search/category
// filter the map uses so the list and the map always agree.
const buildVendorEntries = (cells, searchQuery, selectedCategorySlugs, sortMode) => {
  const normalizedQuery = (searchQuery || '').trim().toLowerCase();
  const hasCategoryFilter = selectedCategorySlugs && selectedCategorySlugs.size > 0;
  const hits = [];
  for (const cell of cells) {
    if (cell.cell_type !== 'booth' || !cell.is_registered || !cell.vendor_name) continue;
    if (hasCategoryFilter && !(cell.category_slug && selectedCategorySlugs.has(cell.category_slug))) {
      continue;
    }
    if (normalizedQuery) {
      const haystack = [cell.vendor_name, cell.category_name, cell.cell_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(normalizedQuery)) continue;
    }
    hits.push(cell);
  }
  hits.sort((a, b) => {
    if (sortMode === 'category') {
      const ca = a.category_name || 'zzz';
      const cb = b.category_name || 'zzz';
      const cmp = ca.localeCompare(cb);
      if (cmp !== 0) return cmp;
    }
    return (a.vendor_name || '').localeCompare(b.vendor_name || '');
  });
  return hits;
};

const VendorRow = ({
  index,
  style,
  entries,
  selectedBoothId,
  favoriteBoothIds,
  onSelectBooth,
  onToggleFavorite,
}) => {
  const cell = entries[index];
  if (!cell) return null;
  const color = getCategoryColor(cell.category_slug);
  const isSelected = cell.id === selectedBoothId;
  const isFavorite = favoriteBoothIds && favoriteBoothIds.has(cell.id);
  return (
    <Box
      style={style}
      onClick={() => onSelectBooth(cell)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 1.5,
        borderBottom: '1px solid #f0ece1',
        cursor: 'pointer',
        background: isSelected ? '#fff8e1' : 'transparent',
        '&:hover': { background: '#faf6e8' },
      }}
    >
      <Box
        sx={{
          width: 8,
          alignSelf: 'stretch',
          background: color,
          borderRadius: 1,
          my: 1,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {cell.vendor_name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {cell.category_name || 'Uncategorized'} · Booth {cell.cell_name}
        </Typography>
      </Box>
      <Tooltip title={isFavorite ? 'Remove favorite' : 'Save favorite'}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite?.(cell.id);
          }}
          aria-label="toggle favorite"
        >
          {isFavorite ? <StarIcon fontSize="small" sx={{ color: '#fbc02d' }} /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
      </Tooltip>
      <Tooltip title="Show on map">
        <LocationOnIcon fontSize="small" sx={{ color: '#9e9e9e' }} />
      </Tooltip>
    </Box>
  );
};

const MapVendorList = ({
  cells,
  searchQuery,
  selectedCategorySlugs,
  selectedBoothId,
  favoriteBoothIds,
  onSelectBooth,
  onToggleFavorite,
  sortMode,
  onSortModeChange,
}) => {
  const listRef = useRef(null);
  const entries = useMemo(
    () => buildVendorEntries(cells, searchQuery, selectedCategorySlugs, sortMode),
    [cells, searchQuery, selectedCategorySlugs, sortMode]
  );

  useEffect(() => {
    if (!selectedBoothId || !listRef.current) return;
    const idx = entries.findIndex((c) => c.id === selectedBoothId);
    if (idx >= 0) {
      try {
        listRef.current.scrollToRow({ index: idx, align: 'smart', behavior: 'smooth' });
      } catch {
        // Index briefly invalid during a re-render; the next effect run handles it.
      }
    }
  }, [selectedBoothId, entries]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#fff',
        borderLeft: '1px solid #ece7d8',
        minWidth: 0,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 1.5, py: 1, borderBottom: '1px solid #f0ece1' }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Vendors
          </Typography>
          <Chip size="small" label={entries.length} variant="outlined" />
        </Stack>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={sortMode}
          onChange={(_, value) => value && onSortModeChange(value)}
        >
          <ToggleButton value="alpha" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>
            A–Z
          </ToggleButton>
          <ToggleButton value="category" sx={{ px: 1, py: 0.25, fontSize: '0.7rem' }}>
            Category
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {entries.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No vendors match your filters.
            </Typography>
          </Box>
        ) : (
          <List
            listRef={listRef}
            rowCount={entries.length}
            rowHeight={ROW_HEIGHT}
            rowComponent={VendorRow}
            rowProps={{
              entries,
              selectedBoothId,
              favoriteBoothIds,
              onSelectBooth,
              onToggleFavorite,
            }}
            style={{ height: '100%', width: '100%' }}
          />
        )}
      </Box>
    </Box>
  );
};

export default MapVendorList;
